/**
 * MyTrips.jsx
 * 
 * Customer profile and booking history page.
 * Similar to Airbnb's profile page but simpler.
 * 
 * Features:
 * - Profile card with name, stats, member since
 * - Editable profile (name, photo)
 * - Booking history with tabs (upcoming, completed, cancelled)
 * - Leave review button on completed trips
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getCityImage } from '../constants/cityImages';
import { toast } from 'sonner';
import {
  User,
  MapPin,
  Calendar,
  Star,
  Clock,
  Users,
  Edit2,
  LogOut,
  ChevronRight,
  Ticket,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Camera,
  Search,
  Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Booking card component
const BookingCard = ({ booking, onViewTicket }) => {
  const navigate = useNavigate();
  
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800',
    no_show: 'bg-gray-100 text-gray-800'
  };

  const statusIcons = {
    pending: AlertCircle,
    confirmed: CheckCircle,
    completed: CheckCircle,
    cancelled: XCircle,
    no_show: XCircle
  };

  const StatusIcon = statusIcons[booking.status] || AlertCircle;
  
  // Navigate to review page for this operator
  const handleReviewClick = () => {
    const slug = booking.organization?.slug;
    if (slug) {
      navigate(`/${slug}/review`);
    } else {
      // Fallback - try to use org ID or show error
      console.error('No organization slug found for review', booking);
      toast.error('Unable to open review page');
    }
  };

  return (
    <Card className="overflow-hidden rounded-2xl shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row">
        {/* Image - Larger */}
        <div className="w-full sm:w-40 h-40 flex-shrink-0 bg-gray-100">
          <img
            src={booking.service_image || 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400&h=400&fit=crop'}
            alt={booking.service_name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 line-clamp-1">
                {booking.service_name}
              </h3>
              {booking.organization && (
                <p className="text-sm text-gray-500">
                  by {booking.organization.name}
                </p>
              )}
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusColors[booking.status]}`}>
              <StatusIcon className="w-3 h-3" />
              {booking.status}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {(() => {
                // Parse date string directly to avoid timezone shifting
                // booking_date comes as "2026-04-03" - we want to display it as-is
                const [year, month, day] = booking.booking_date.split('-').map(Number);
                const date = new Date(year, month - 1, day); // month is 0-indexed
                return date.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                });
              })()}
            </span>
            {booking.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {(() => {
                  const [hours, minutes] = booking.start_time.split(':').map(Number);
                  const period = hours >= 12 ? 'PM' : 'AM';
                  const hour12 = hours % 12 || 12;
                  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
                })()}
              </span>
            )}
            {(booking.city || booking.organization?.city) && (
              <span className="flex items-center gap-1 text-gray-500">
                <MapPin className="w-3 h-3" />
                {booking.city || booking.organization?.city}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {booking.party_size} {booking.party_size === 1 ? 'guest' : 'guests'}
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="font-semibold text-gray-900">
              {booking.currency} {booking.total_amount?.toFixed(2)}
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs rounded-full"
                onClick={() => onViewTicket()}
                data-testid={`view-ticket-${booking.id}`}
              >
                <Ticket className="w-3 h-3 mr-1" />
                Ticket
              </Button>
              {booking.can_review && (
                <Button 
                  size="sm" 
                  className="text-xs rounded-full bg-amber-500 hover:bg-amber-600"
                  onClick={handleReviewClick}
                >
                  <Star className="w-3 h-3 mr-1" />
                  Review
                </Button>
              )}
              {booking.has_reviewed && (
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-xs rounded-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  onClick={handleReviewClick}
                >
                  <Star className="w-3 h-3 mr-1 fill-amber-400 text-amber-400" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Edit profile modal
const EditProfileModal = ({ customer, open, onClose, onSave }) => {
  const [name, setName] = useState(customer?.name || '');
  const [city, setCity] = useState(customer?.city || '');
  const [bio, setBio] = useState(customer?.bio || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customer) {
      setName(customer.name || '');
      setCity(customer.city || '');
      setBio(customer.bio || '');
    }
  }, [customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({ name, city, bio });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">City</label>
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Where are you from?"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">About me</label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell us about yourself..."
              rows={3}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const MyTrips = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, isAuthenticated, loading: authLoading, logout, updateProfile } = useCustomerAuth();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming'); // Default to upcoming
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cities, setCities] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);
  const tripsRef = useRef(null); // Ref for scrolling to trips section
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 10; // Load 10 at a time for better performance

  // Check if user data was passed from auth callback
  const userFromCallback = location.state?.user;
  
  // Scroll to trips section helper
  const scrollToTrips = () => {
    if (tripsRef.current) {
      tripsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Fetch cities for search dropdown
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${API_URL}/api/marketplace/cities`);
        if (response.ok) {
          const data = await response.json();
          setCities(data.cities || []);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };
    fetchCities();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Redirect if not authenticated (skip if user data passed from callback)
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !userFromCallback) {
      navigate('/?signin=true');
    }
  }, [authLoading, isAuthenticated, navigate, userFromCallback]);

  // Fetch bookings - reset pagination when tab changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings(true); // Reset pagination on tab change
    }
  }, [isAuthenticated, activeTab]);

  const fetchBookings = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const currentOffset = reset ? 0 : offset;
      const statusParam = activeTab === 'all' ? '' : `&status=${activeTab}`;
      const response = await fetch(
        `${API_URL}/api/customer/bookings?limit=${LIMIT}&offset=${currentOffset}${statusParam}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        const newBookings = data.bookings || [];
        
        if (reset) {
          setBookings(newBookings);
        } else {
          setBookings(prev => [...prev, ...newBookings]);
        }
        
        setTotalCount(data.total || 0);
        setHasMore(newBookings.length === LIMIT && (currentOffset + newBookings.length) < (data.total || 0));
        setOffset(currentOffset + newBookings.length);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchBookings(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const memberSince = customer?.member_since 
    ? new Date(customer.member_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Recently';

  // Filter cities based on search input
  const filteredCities = cities.filter(city => 
    city.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (city.country && city.country.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-9 w-9 object-contain rounded-xl"
              />
              <span className="text-xl font-bold text-gray-900 hidden sm:inline">BookingFlows</span>
            </Link>
            
            {/* Header Search Bar with Dropdown */}
            <div ref={searchRef} className="flex-1 max-w-xl hidden md:block relative">
              <form onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                }
              }}>
                <div className="flex items-center gap-2 p-1.5 bg-white rounded-full border border-gray-200">
                  <div className="flex-1 flex items-center gap-2 pl-3">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSearchDropdown(true);
                      }}
                      onFocus={() => setShowSearchDropdown(true)}
                      placeholder="Where to?"
                      className="flex-1 border-0 outline-none text-sm placeholder:text-gray-400 bg-transparent"
                      data-testid="header-search-input"
                    />
                  </div>
                  <div className="hidden sm:flex items-center gap-1 px-2 text-xs text-gray-400 border-l border-gray-200">
                    <Globe className="w-3 h-3" />
                    <span>USA</span>
                  </div>
                  <Button 
                    type="submit"
                    size="sm"
                    className="rounded-full px-4 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* City Dropdown */}
              {showSearchDropdown && (filteredCities.length > 0 || cities.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 z-[100]">
                  <div className="p-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {searchQuery ? 'Matching Cities' : 'Popular Destinations'}
                    </p>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {(searchQuery ? filteredCities : cities).slice(0, 5).map((city) => (
                      <button
                        key={city.city}
                        onClick={() => {
                          setShowSearchDropdown(false);
                          navigate(`/search?city=${encodeURIComponent(city.city)}`);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="w-12 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={getCityImage(city.city)}
                            alt={city.city}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{city.city}</p>
                          <p className="text-xs text-gray-500">{city.country}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400">{city.operatorCount} {city.operatorCount === 1 ? 'provider' : 'providers'}</p>
                        </div>
                      </button>
                    ))}
                    {filteredCities.length === 0 && searchQuery && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <p>No destinations found for &quot;{searchQuery}&quot;</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 bg-gray-50">
                {customer?.avatar_url ? (
                  <img 
                    src={customer.avatar_url} 
                    alt={customer.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
                <span className="hidden sm:inline text-sm font-medium text-gray-700 pr-1">
                  {customer?.name?.split(' ')[0] || 'Profile'}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-600">
                <LogOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Sign out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card - Left Sidebar */}
          <div className="lg:col-span-1">
            <Card 
              className="sticky top-24 rounded-3xl"
              style={{ boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.1)' }}
            >
              <CardContent className="pt-6">
                {/* Avatar */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {customer?.avatar_url ? (
                        <img 
                          src={customer.avatar_url} 
                          alt={customer.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-12 h-12 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Name & Email */}
                  <h2 className="mt-4 text-xl font-semibold text-gray-900">
                    {customer?.name || 'Traveler'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {customer?.email}
                  </p>

                  {/* Edit button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4 rounded-full"
                    onClick={() => setEditModalOpen(true)}
                  >
                    <Edit2 className="w-3 h-3 mr-2" />
                    Edit profile
                  </Button>
                </div>

                {/* Stats - Clickable */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <button 
                      onClick={() => {
                        setActiveTab('upcoming');
                        scrollToTrips();
                      }}
                      className="p-3 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <p className="text-2xl font-bold text-gray-900 mb-1">
                        {customer?.total_trips || 0}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                        <Ticket className="w-3 h-3 text-blue-500" />
                        My Trips
                      </p>
                    </button>
                    <button 
                      onClick={() => {
                        setActiveTab('completed');
                        scrollToTrips();
                      }}
                      className="p-3 rounded-xl hover:bg-gray-100 transition-colors"
                      title="View trips you've reviewed"
                    >
                      <p className="text-2xl font-bold text-gray-900 mb-1">
                        {customer?.total_reviews || 0}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        Reviews
                      </p>
                    </button>
                  </div>
                </div>

                {/* Member since - simplified */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 text-center">
                    Member since {memberSince}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bookings - Main Content */}
          <div className="lg:col-span-2" ref={tripsRef}>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">My Trips</h1>
              <p className="text-gray-500">View and manage your bookings</p>
            </div>

            {/* Tabs - Mobile friendly with horizontal scroll */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="w-full sm:w-auto overflow-x-auto flex-nowrap">
                <TabsTrigger value="upcoming" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">Upcoming</TabsTrigger>
                <TabsTrigger value="completed" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">Completed</TabsTrigger>
                <TabsTrigger value="cancelled" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">Cancelled</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Bookings list */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <div className="flex">
                      <Skeleton className="w-32 h-32" />
                      <div className="flex-1 p-4 space-y-3">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-4 w-1/3" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : bookings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Ticket className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No trips yet
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {activeTab === 'upcoming' 
                      ? "No upcoming trips. Time to book an adventure!"
                      : activeTab === 'completed'
                      ? "No completed trips yet."
                      : "No cancelled trips."}
                  </p>
                  <Link to="/">
                    <Button>
                      Explore experiences
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onViewTicket={async () => {
                      // Generate magic link for direct ticket access
                      const confirmationCode = booking.confirmation_code || booking.id;
                      try {
                        const response = await fetch(
                          `${API_URL}/api/v2/public/bookings/${confirmationCode}/generate-magic-link`,
                          { method: 'POST' }
                        );
                        const data = await response.json();
                        if (data.magicLink) {
                          // Navigate to the magic link URL
                          window.location.href = data.magicLink;
                        } else {
                          // Fallback to direct navigation
                          navigate(`/customer-ticket/${confirmationCode}`);
                        }
                      } catch (error) {
                        console.error('Failed to generate magic link:', error);
                        // Fallback to direct navigation
                        navigate(`/customer-ticket/${confirmationCode}`);
                      }
                    }}
                  />
                ))}
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="outline"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="px-8"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Load More
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Total count indicator */}
                {bookings.length > 0 && (
                  <p className="text-center text-sm text-gray-500 pt-2">
                    Showing {bookings.length} of {totalCount} trips
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        customer={customer}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={updateProfile}
      />

      {/* Footer - Simple version matching marketplace */}
      <footer className="py-12 px-4 bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-8 w-8 object-contain rounded-xl"
              />
              <span className="text-lg font-bold text-gray-900">BookingFlows</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <Link to="/search" className="hover:text-blue-600 transition-colors">All Experiences</Link>
              <Link to="/for-suppliers" className="hover:text-blue-600 transition-colors">For Operators</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} BookingFlows
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MyTrips;
